import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { getCommerceId } from '@/lib/ownership';
import { getOrganizationByCommerce, type CoreOrganization } from '@/lib/zifekCore';
import {
  fetchSearchStats,
  listRecentSearches,
  logSearch,
  searchGlobal,
  type CoreSearchHistory,
  type SearchGroup,
} from '@/lib/coreSearch';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 44. GLOBAL SEARCH
// Barre de recherche globale du dashboard professionnel.
// Ex : « Ahmed » → Clients, Commandes, Factures, Rendez-vous, Documents.
// Les résultats sont FILTRÉS PAR PERMISSIONS (via usePermissions).
// ─────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

export default function GlobalSearchBar({ className }: Props) {
  const { user } = useAuth();
  const { has } = usePermissions();
  const navigate = useNavigate();
  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<CoreSearchHistory[]>([]);
  const [indexCount, setIndexCount] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!commerceId) return;
    let cancelled = false;
    getOrganizationByCommerce(commerceId)
      .then((o) => { if (!cancelled) setOrg(o); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [commerceId]);

  const loadRecent = useCallback(() => {
    if (!org) return;
    listRecentSearches(org.id, 8).then(setRecent).catch(() => {});
    fetchSearchStats(org.id).then((s) => setIndexCount(s.total)).catch(() => {});
  }, [org]);

  useEffect(() => { if (open) loadRecent(); }, [open, loadRecent]);

  // Recherche avec petit délai (évite une requête à chaque frappe).
  useEffect(() => {
    if (!org) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setGroups([]);
      setTotal(0);
      return undefined;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      searchGlobal({ organizationId: org.id, query: q, canAccess: (p) => has(p), limit: 40, userId: user?.id ?? null })
        .then((res) => {
          setGroups(res.groups);
          setTotal(res.total);
        })
        .catch(() => { setGroups([]); setTotal(0); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, org, has]);

  // Ferme au clic en dehors
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = (value: string) => {
    const q = value.trim();
    if (!q || !org) return;
    // Consigne la recherche dans l'historique.
    logSearch({
      tenantId: org.tenant_id,
      organizationId: org.id,
      userId: user?.id ?? null,
      query: q,
      resultCount: total,
    }).then(loadRecent).catch(() => {});
  };

  const goTo = (url: string | null, title: string) => {
    runSearch(title);
    setOpen(false);
    setQuery('');
    if (url) navigate(url);
  };

  return (
    <div ref={boxRef} className={`relative ${className || ''}`}>
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { runSearch(query); }
            if (e.key === 'Escape') { setOpen(false); }
          }}
          placeholder="Rechercher partout… (client, commande, facture…)"
          className="w-full pl-9 pr-3 py-2 rounded-full text-sm bg-background-100 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
        />
        {loading && (
          <i className="ri-loader-4-line animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden z-50 max-h-[70vh] flex flex-col">
          <div className="px-4 h-9 flex items-center justify-between border-b border-background-200/60 flex-shrink-0">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400">Recherche globale</span>
            <span className="text-[11px] text-foreground-400">{indexCount} élément(s) indexés</span>
          </div>

          <div className="overflow-y-auto flex-1">
            {query.trim().length < 2 ? (
              recent.length > 0 ? (
                <div className="py-2">
                  <p className="px-4 py-1 text-[11px] font-semibold text-foreground-400">Recherches récentes</p>
                  {recent.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => { setQuery(r.query); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-history-line text-foreground-400"></i>
                      <span className="flex-1 truncate">{r.query}</span>
                      <span className="text-[11px] text-foreground-400">{r.result_count ?? 0}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-10 text-center">
                  <i className="ri-search-eye-line text-2xl text-foreground-300"></i>
                  <p className="mt-2 text-sm text-foreground-500">Tapez au moins 2 caractères pour chercher dans clients, commandes, factures, rendez-vous et documents.</p>
                </div>
              )
            ) : groups.length === 0 && !loading ? (
              <div className="px-6 py-10 text-center">
                <i className="ri-emotion-sad-line text-2xl text-foreground-300"></i>
                <p className="mt-2 text-sm text-foreground-500">Aucun résultat pour « {query.trim()} ».</p>
                <p className="mt-1 text-xs text-foreground-400">Les résultats dépendent de vos permissions.</p>
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.key} className="py-1.5">
                  <div className="px-4 py-1 flex items-center gap-2">
                    <span className={`w-5 h-5 rounded flex items-center justify-center ${g.className}`}><i className={`${g.icon} text-[11px]`}></i></span>
                    <span className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider">{g.label}</span>
                    <span className="text-[11px] text-foreground-400">({g.results.length})</span>
                  </div>
                  {g.results.map((r) => (
                    <button
                      key={`${r.entity_type}-${r.entity_id}`}
                      onClick={() => goTo(r.url, r.title)}
                      className="w-full flex items-start gap-2.5 px-4 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className={`${r.typeIcon} text-foreground-400 mt-0.5`}></i>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-foreground-800 truncate">{r.title}</p>
                        {r.subtitle && <p className="text-[11px] text-foreground-400 truncate">{r.subtitle}</p>}
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300 mt-0.5"></i>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}